/** A chord of the platform's primary modifier (Command on Apple devices, Control elsewhere) and one character key. */
export interface KeyboardShortcut {
  readonly key: string
}

/** Which primary modifier a device expects for a shortcut chord: Command (`apple`) or Control (`other`). */
export type ShortcutPlatform = 'apple' | 'other'

/** The subset of `Navigator` platform detection reads from, kept minimal so a test can fake it. */
export interface ShortcutNavigatorLike {
  userAgentData?: { platform?: string }
  platform?: string
  userAgent?: string
}

const APPLE_PLATFORM_PATTERN = /mac|iphone|ipad|ipod/i

/** Tells an Apple device from any other from the platform string, defaulting to `other`. */
export function detectShortcutPlatform(
  navigatorLike: ShortcutNavigatorLike | undefined = globalThis.navigator,
): ShortcutPlatform {
  if (!navigatorLike) return 'other'
  const source =
    navigatorLike.userAgentData?.platform ?? navigatorLike.platform ?? navigatorLike.userAgent
  return source !== undefined && APPLE_PLATFORM_PATTERN.test(source) ? 'apple' : 'other'
}

/** Whether a keydown event is exactly this shortcut's chord: no repeat, no Shift/Alt, only the primary modifier, same key case-insensitively. */
export function matchesShortcut(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'repeat'>,
  shortcut: KeyboardShortcut,
  platform: ShortcutPlatform,
): boolean {
  if (event.repeat || event.altKey || event.shiftKey) return false
  const primaryModifierHeld =
    platform === 'apple' ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey
  return primaryModifierHeld && event.key.toLowerCase() === shortcut.key.toLowerCase()
}

/** The shortcut in WAI-ARIA `aria-keyshortcuts` syntax, e.g. `Meta+J` or `Control+J`. */
export function ariaKeyShortcuts(shortcut: KeyboardShortcut, platform: ShortcutPlatform): string {
  const modifier = platform === 'apple' ? 'Meta' : 'Control'
  return `${modifier}+${shortcut.key.toUpperCase()}`
}

/** The shortcut's display label: `⌘J` on Apple devices, `${controlKeyName}+J` otherwise. */
export function formatShortcut(
  shortcut: KeyboardShortcut,
  platform: ShortcutPlatform,
  controlKeyName: string,
): string {
  return platform === 'apple'
    ? `⌘${shortcut.key.toUpperCase()}`
    : `${controlKeyName}+${shortcut.key.toUpperCase()}`
}
